import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import {
  type CellClickedEvent,
  type ColDef,
  type ICellRendererParams
} from "ag-grid-community";
import { Delete01Icon, PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { toast } from "react-toastify";
import { PropertyKind } from "shared/types";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Select,
  Show
} from "shared/components";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { Card } from "shared/components/Card";
import { Pagination } from "shared/components/Pagination";
import { AgGrid } from "shared/components/AgGrid";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { ApartmentsComVariant } from "types/landlord/address";
import {
  FEED_ENABLED_AT_FIELD,
  FeedListing,
  SyndicationFeed
} from "api/syndicationFeed";
import { AddListingsModal } from "./AddListingsModal";
import { ADD_LISTINGS_LABEL } from "./constants";
import { getFeedErrorMessage } from "./feedErrorMessage";
import { useFeedListings, useToggleFeedListing } from "./useSyndicationFeed";

interface FeedListingsCardProps {
  feed: SyndicationFeed;
  feedLabel: string;
}

const ROW_HEIGHT = 72;
const EMPTY_LISTINGS: FeedListing[] = [];

const APARTMENTS_VARIANT_OPTIONS: {
  value: ApartmentsComVariant;
  label: string;
}[] = [
  { value: "single_family", label: "Single family" },
  { value: "multi_family", label: "Multi family" }
];

const spotlightHeader = (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-content-300">Spotlight</span>
    <Badge variant={BadgeVariant.PRIMARY} size={BadgeSize.SM}>
      Coming soon
    </Badge>
  </div>
);

const ListedOnCell = ({ value }: { value: string | null }) => {
  const date = value ? DateTime.fromISO(value) : null;
  if (!date?.isValid)
    return <span className="text-sm text-content-400">—</span>;

  return (
    <div className="space-y-2">
      <p className="text-sm text-content-1000">
        {date.toFormat("MMM d, yyyy")}
      </p>
      <span className="text-xs block text-content-400">
        {date.toFormat("h:mm a")}
      </span>
    </div>
  );
};

export const FeedListingsCard = ({
  feed,
  feedLabel
}: FeedListingsCardProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const { data, isLoading } = useFeedListings(feed, page);
  const removeListing = useToggleFeedListing(feed);
  const variantUpdate = useToggleFeedListing(feed);
  const enabledAtField = FEED_ENABLED_AT_FIELD[feed];

  const items = data?.items ?? EMPTY_LISTINGS;
  const total = data?.total_count ?? 0;
  const totalPages = data?.page_count ?? 1;

  const handleRemove = (listing: FeedListing) => {
    if (!listing.unit) return;

    const isLastRowOnPage = items.length === 1 && page > 1;

    removeListing.mutate(
      { unitId: listing.unit.id, enabled: false },
      {
        onSuccess: () => {
          toast.success("Listing removed from feed");
          if (isLastRowOnPage) setPage((current) => current - 1);
        },
        onError: (error) => toast.error(getFeedErrorMessage(error))
      }
    );
  };

  const handleChangeVariant = (
    listing: FeedListing,
    variant: ApartmentsComVariant
  ) => {
    if (!listing.unit || variant === listing.feed_apartments_com_variant)
      return;

    variantUpdate.mutate(
      { unitId: listing.unit.id, enabled: true, variant },
      {
        onSuccess: () => toast.success("Listing type updated"),
        onError: (error) => toast.error(getFeedErrorMessage(error))
      }
    );
  };

  const onCellClicked = useCallback(
    (event: CellClickedEvent<FeedListing>) => {
      const row = event.data;
      if (!row) return;

      const colId = event.column.getColId();
      if (colId === "actions" || colId === "spotlight" || colId === "type")
        return;

      const { id: propertyId, kind } = row.property;
      if (kind === PropertyKind.SingleFamily || !row.unit)
        navigate(`/properties/${propertyId}`);
      else navigate(`/properties/${propertyId}/units/${row.unit.id}`);
    },
    [navigate]
  );

  const columns = useMemo<ColDef<FeedListing>[]>(
    () => [
      {
        headerName: "Property",
        colId: "property",
        flex: 2,
        minWidth: 240,
        cellRenderer: (params: ICellRendererParams<FeedListing>) => {
          const listing = params.data;
          if (!listing) return null;
          return (
            <div className="min-w-0">
              <p className="truncate text-sm text-content-1000 font-medium">
                {listing.property.name}
              </p>
              <Show if={Boolean(listing.property.address?.one_line_address)}>
                <p className="truncate text-xs text-content-400">
                  {listing.property.address?.one_line_address}
                </p>
              </Show>
            </div>
          );
        }
      },
      {
        headerName: "Unit",
        colId: "unit",
        flex: 1,
        minWidth: 120,
        cellRenderer: (params: ICellRendererParams<FeedListing>) => (
          <p className="text-sm text-content-1000 font-medium">
            {params.data?.unit?.title ?? "—"}
          </p>
        )
      },
      ...(feed === "zillow"
        ? [
            {
              headerName: "Spotlight",
              colId: "spotlight",
              flex: 1,
              minWidth: 160,
              headerComponent: () => spotlightHeader,
              cellRenderer: () => (
                <Switch
                  size={SwitchSize.SM}
                  checked={false}
                  disabled
                  onChange={() => undefined}
                />
              )
            }
          ]
        : []),
      ...(feed === "apartments_com"
        ? [
            {
              headerName: "Type",
              colId: "type",
              flex: 1,
              minWidth: 240,
              cellRenderer: (params: ICellRendererParams<FeedListing>) => {
                const listing = params.data;
                if (!listing?.unit) return null;
                const isUpdating =
                  variantUpdate.isPending &&
                  variantUpdate.variables?.unitId === listing.unit.id;
                return (
                  <div onClick={(event) => event.stopPropagation()}>
                    <Select
                      id={`apartments-type-${listing.unit.id}`}
                      size={InputSize.SM}
                      className="!rounded-lg w-[192px]"
                      value={listing.feed_apartments_com_variant ?? undefined}
                      options={APARTMENTS_VARIANT_OPTIONS}
                      isDisabled={isUpdating}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      legacyErrorMargins={false}
                      onChange={(event) =>
                        handleChangeVariant(
                          listing,
                          String(event.target.value) as ApartmentsComVariant
                        )
                      }
                    />
                  </div>
                );
              }
            }
          ]
        : []),
      {
        headerName: "Listed on",
        colId: "listed_on",
        flex: 1,
        minWidth: 160,
        cellRenderer: (params: ICellRendererParams<FeedListing>) => (
          <ListedOnCell value={params.data?.[enabledAtField] ?? null} />
        )
      },
      {
        headerName: "",
        colId: "actions",
        width: 72,
        cellRenderer: (params: ICellRendererParams<FeedListing>) => {
          const listing = params.data;
          if (!listing) return null;
          const isRemoving =
            removeListing.isPending &&
            removeListing.variables?.unitId === listing.unit?.id;
          return (
            <IconButton
              variant={IconButtonVariant.TEXT_BASE}
              size={IconButtonSize.SM}
              HugeIcon={Delete01Icon}
              loading={isRemoving}
              disabled={removeListing.isPending}
              onClick={() => handleRemove(listing)}
              className="!text-content-1000"
            />
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      feed,
      enabledAtField,
      removeListing.isPending,
      removeListing.variables?.unitId,
      variantUpdate.isPending,
      variantUpdate.variables?.unitId,
      page,
      items.length
    ]
  );

  return (
    <Card className="w-full p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold text-content-1000">
            Listings in feed
          </h3>
          <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM}>
            {total}
          </Badge>
        </div>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          HugeIcon={PlusSignIcon}
          onClick={() => setIsAddOpen(true)}
        >
          {ADD_LISTINGS_LABEL}
        </Button>
      </div>

      <div className="mt-2">
        <AgGrid<FeedListing>
          columns={columns}
          rows={items}
          loading={isLoading}
          rowHeight={ROW_HEIGHT}
          wrapperClassName="!rounded-none !border-0"
          themeOverrides={{ "--ag-cell-horizontal-padding": "0px" }}
          getRowId={(params) => String(params.data.id)}
          onCellClicked={onCellClicked}
          noRowsFallback={
            <NoRowsFallback
              title="No listings in feed"
              text={`No listings are being passed in the ${feedLabel} feed yet.`}
            />
          }
        />
      </div>

      <Pagination
        className="mt-6 justify-end"
        total={totalPages}
        current={page}
        onChange={setPage}
      />

      <AddListingsModal
        feed={feed}
        feedLabel={feedLabel}
        opened={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      />
    </Card>
  );
};
